import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { OriginalsNav } from '@/components/OriginalsNav';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Lock, CreditCard, Truck, Tag, X, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OriginalsCheckout() {
  const navigate = useNavigate();
  const { cart, getTotal, clearCart } = useCart();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB'
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);

  // Calculate shipping based on products and country
  useEffect(() => {
    calculateShipping();
  }, [cart, formData.country]);

  const calculateShipping = async () => {
    if (cart.length === 0) {
      setShippingCost(0);
      return;
    }

    try {
      const response = await axios.post(`${API}/calculate-shipping`, {
        items: cart.map(item => ({ product_id: item.product.id, quantity: item.quantity })),
        country: formData.country
      });
      setShippingCost(response.data.shipping_cost);
    } catch (error) {
      // Fallback to default shipping
      setShippingCost(formData.country === 'GB' ? 0 : 15);
    }
  };

  const getSubtotal = () => {
    return getTotal();
  };

  const getDiscountAmount = () => {
    return appliedDiscount?.discount_amount || 0;
  };

  const getOrderTotal = () => {
    return getSubtotal() + shippingCost - getDiscountAmount();
  };

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    if (!formData.email.trim()) {
      toast.error('Please enter your email first');
      return;
    }

    setIsApplyingDiscount(true);
    try {
      const response = await axios.post(`${API}/discount/apply`, {
        code: discountCode.trim(),
        email: formData.email,
        subtotal: getSubtotal()
      });
      
      setAppliedDiscount(response.data);
      toast.success(`Discount applied: ${response.data.discount_type === 'percentage' ? `${response.data.discount_value}%` : `£${response.data.discount_value}`} off!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.address_line1 || !formData.city || !formData.postcode) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setIsProcessing(true);
    try {
      const items = cart.map(item => ({
        product_id: item.product.id,
        quantity: item.quantity,
        selected_options: item.selectedOptions || {}
      }));

      const response = await axios.post(`${API}/orders/checkout`, {
        items,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        shipping_address: {
          line1: formData.address_line1,
          line2: formData.address_line2,
          city: formData.city,
          county: formData.county,
          postcode: formData.postcode,
          country: formData.country
        },
        shipping_country: formData.country,
        shipping_rate: shippingCost,
        discount_code: appliedDiscount?.code || null,
        discount_amount: getDiscountAmount(),
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to initiate checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <OriginalsNav />
        <div className="pt-32 pb-20 px-6 text-center">
          <h1 className="font-playfair text-3xl text-white mb-4">Your Cart is Empty</h1>
          <Link to="/originals" className="text-[#d4af37] hover:underline font-cormorant">
            Return to Gallery
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="originals-checkout">
      <OriginalsNav />
      
      <main className="pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          {/* Back Link */}
          <Link 
            to="/originals/cart"
            className="inline-flex items-center gap-2 text-[#a1a1aa] hover:text-[#d4af37] font-cormorant mb-8 transition-colors"
            data-testid="back-to-cart"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Cart
          </Link>

          <h1 className="font-playfair text-3xl lg:text-4xl text-white mb-12">Checkout</h1>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Customer Info Form */}
            <div>
              <h2 className="font-cinzel text-xs tracking-widest text-[#d4af37] mb-6">CONTACT INFORMATION</h2>
              
              <form onSubmit={handleCheckout} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-cormorant text-[#a1a1aa]">
                      Full Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                      required
                      data-testid="checkout-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-cormorant text-[#a1a1aa]">
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="07xxx xxxxxx"
                      className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="font-cormorant text-[#a1a1aa]">
                    Email Address <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                    required
                    data-testid="checkout-email-input"
                  />
                </div>

                <h2 className="font-cinzel text-xs tracking-widest text-[#d4af37] mt-8 mb-4 pt-6 border-t border-[#27272a]">SHIPPING ADDRESS</h2>

                <div className="space-y-2">
                  <Label htmlFor="address1" className="font-cormorant text-[#a1a1aa]">
                    Address Line 1 <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="address1"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    placeholder="House number and street"
                    className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address2" className="font-cormorant text-[#a1a1aa]">
                    Address Line 2
                  </Label>
                  <Input
                    id="address2"
                    value={formData.address_line2}
                    onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                    placeholder="Apartment, suite, etc. (optional)"
                    className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="font-cormorant text-[#a1a1aa]">
                      City / Town <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="City"
                      className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="county" className="font-cormorant text-[#a1a1aa]">
                      County / State
                    </Label>
                    <Input
                      id="county"
                      value={formData.county}
                      onChange={(e) => setFormData({ ...formData, county: e.target.value })}
                      placeholder="County"
                      className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="postcode" className="font-cormorant text-[#a1a1aa]">
                      Postcode / ZIP <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="postcode"
                      value={formData.postcode}
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                      placeholder="NP25 xxx"
                      className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37] placeholder:text-[#52525b]"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="font-cormorant text-[#a1a1aa]">
                      Country <span className="text-red-400">*</span>
                    </Label>
                    <Select 
                      value={formData.country} 
                      onValueChange={(value) => setFormData({ ...formData, country: value })}
                    >
                      <SelectTrigger 
                        className="bg-[#171717] border-[#27272a] text-white focus:border-[#d4af37] focus:ring-[#d4af37]"
                        data-testid="checkout-country-select"
                      >
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#171717] border-[#27272a]">
                        <SelectItem value="GB" className="text-white hover:bg-[#27272a]">United Kingdom</SelectItem>
                        <SelectItem value="US" className="text-white hover:bg-[#27272a]">United States</SelectItem>
                        <SelectItem value="CA" className="text-white hover:bg-[#27272a]">Canada</SelectItem>
                        <SelectItem value="AU" className="text-white hover:bg-[#27272a]">Australia</SelectItem>
                        <SelectItem value="DE" className="text-white hover:bg-[#27272a]">Germany</SelectItem>
                        <SelectItem value="FR" className="text-white hover:bg-[#27272a]">France</SelectItem>
                        <SelectItem value="ES" className="text-white hover:bg-[#27272a]">Spain</SelectItem>
                        <SelectItem value="IT" className="text-white hover:bg-[#27272a]">Italy</SelectItem>
                        <SelectItem value="NL" className="text-white hover:bg-[#27272a]">Netherlands</SelectItem>
                        <SelectItem value="IE" className="text-white hover:bg-[#27272a]">Ireland</SelectItem>
                        <SelectItem value="OTHER" className="text-white hover:bg-[#27272a]">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="pt-6 border-t border-[#27272a]">
                  <div className="flex items-center gap-2 text-[#a1a1aa] mb-6">
                    <Lock className="w-4 h-4" />
                    <span className="font-cormorant text-sm">Secure payment powered by Stripe</span>
                  </div>

                  <Button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full py-6 rounded-full bg-[#d4af37] text-black hover:bg-[#cd7f32] font-cinzel tracking-wider disabled:opacity-50"
                    data-testid="pay-now-btn"
                  >
                    {isProcessing ? (
                      'Processing...'
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay £{Math.floor(getOrderTotal())}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div>
              <h2 className="font-cinzel text-xs tracking-widest text-[#d4af37] mb-6">ORDER SUMMARY</h2>
              
              <div className="bg-[#171717] rounded-lg border border-[#27272a] p-6">
                {/* Items */}
                <div className="space-y-4 mb-6">
                  {cart.map((item, index) => (
                    <div key={`${item.product.id}-${index}`} className="flex gap-4">
                      <img 
                        src={item.product.image_url} 
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-grow">
                        <p className="font-playfair text-white">{item.product.name}</p>
                        {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                          <div className="space-y-0.5">
                            {Object.entries(item.selectedOptions).map(([key, value]) => (
                              <p key={key} className="font-cormorant text-xs text-[#a1a1aa] capitalize">
                                {key}: {value}
                              </p>
                            ))}
                          </div>
                        )}
                        <p className="font-cormorant text-sm text-[#a1a1aa]">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-mono text-white">
                        £{Math.floor(item.product.price * item.quantity)}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Discount Code */}
                <div className="border-t border-[#27272a] pt-6 mt-6">
                  <Label className="font-cormorant text-[#a1a1aa] mb-2 block">
                    Discount Code
                  </Label>
                  {appliedDiscount ? (
                    <div className="flex items-center justify-between bg-[#171717] border border-[#d4af37] rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#d4af37]" />
                        <span className="font-mono text-[#d4af37]">{appliedDiscount.code}</span>
                        <span className="text-gray-400 text-sm">
                          (-£{Math.floor(appliedDiscount.discount_amount)})
                        </span>
                      </div>
                      <button 
                        type="button" 
                        onClick={removeDiscount}
                        className="text-gray-400 hover:text-white"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="bg-[#171717] border-[#27272a] text-white uppercase flex-1"
                      />
                      <Button
                        type="button"
                        onClick={applyDiscountCode}
                        disabled={isApplyingDiscount}
                        variant="outline"
                        className="border-[#27272a] text-white hover:bg-[#27272a]"
                      >
                        {isApplyingDiscount ? '...' : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Totals */}
                <div className="border-t border-[#27272a] pt-6 space-y-3 mt-6">
                  <div className="flex justify-between">
                    <span className="font-cormorant text-[#a1a1aa]">Subtotal</span>
                    <span className="font-mono text-white">£{Math.floor(getSubtotal())}</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex justify-between">
                      <span className="font-cormorant text-[#d4af37]">Discount ({appliedDiscount.code})</span>
                      <span className="font-mono text-[#d4af37]">-£{Math.floor(getDiscountAmount())}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="font-cormorant text-[#a1a1aa]">Shipping</span>
                    {shippingCost === 0 ? (
                      <span className="font-cormorant text-[#d4af37]">FREE</span>
                    ) : (
                      <span className="font-mono text-white">£{shippingCost}</span>
                    )}
                  </div>
                  <div className="flex justify-between pt-3 border-t border-[#27272a]">
                    <span className="font-cinzel tracking-wider text-white">Total</span>
                    <span className="font-mono text-xl text-[#d4af37]">£{Math.floor(getOrderTotal())}</span>
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="mt-6 flex items-center justify-center gap-6 text-[#a1a1aa]">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  <span className="font-cormorant text-xs">SSL Secured</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  <span className="font-cormorant text-xs">Stripe Payments</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#27272a] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="font-cormorant text-sm text-[#a1a1aa] text-center">
            © 2024 Ben Jefferies Originals. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
